import { Box, Avatar, Typography, Link, Button, IconButton, ExpandLessIcon, ExpandMoreIcon } from 'src/components';
import { useIsMobile } from 'src/hooks';
import { useState, useEffect } from 'react';

export default function UserDisplayCard({ isDesktopWideView, isDesktopViewExpanded, profile }) {
    const isMobile = useIsMobile();
    const [isExpanded, setIsExpanded] = useState(true);

    const desktopLeftPadding = !isDesktopViewExpanded ? 250 : (isDesktopWideView ? 470 : 400);
    const desktopTopPadding = 25;

    const maxBioLength = 1400; //Truncate basically removed but logic kept
    const truncatedBio = profile.bio?.length > maxBioLength
        ? `${profile.bio.slice(0, maxBioLength)}...`
        : profile.bio;

    const hasBio = Boolean(truncatedBio);

    // Auto-collapse after 10 seconds when expanded
    useEffect(() => {
        if (isExpanded && hasBio) {
            const timer = setTimeout(() => {
                setIsExpanded(false);
            }, 10000);
            return () => clearTimeout(timer);
        }
    }, [isExpanded, hasBio]);

    if (!profile) return null;

    const handleToggle = () => {
        if (hasBio) {
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <Box
            sx={{
                width: '90%',
                position: 'absolute',
                top: isMobile ? 'calc(20px + var(--safe-area-top))' : desktopTopPadding,
                left: isMobile ? '50%' : desktopLeftPadding,
                transform: isMobile ? 'translateX(-50%)' : 'none',
                transition: 'all 0.4s ease-in-out',
                maxWidth: '400px',
                zIndex: 1,
            }}
        >
            <Box
                sx={{
                    bgcolor: '#fff',
                    borderRadius: isMobile ? 4 : 8,
                    boxShadow: 2,
                    px: 1.5,
                    py: 1,
                    transition: 'height 0.3s ease-out',
                    overflow: 'hidden',
                }}
            >
                {/* Main row - always visible */}
                <Box
                    onClick={handleToggle}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        cursor: hasBio ? 'pointer' : 'default',
                        minHeight: '40px',
                    }}
                >
                    {(!isExpanded || !isMobile) && (
                        <Avatar 
                            src={profile.profileImageUrl} 
                            alt={profile.name}
                            sx={{ width: 32, height: 32 }}
                        >
                            {profile.name?.charAt(0)}
                        </Avatar>
                    )}

                    <Box sx={{ flex: 1, minWidth: 0, ml: !isExpanded ? 0 : 1 }}>
                        <Typography variant="body2" sx={{ 
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                        }}>
                            <Box component="span" fontWeight="bold" sx={{ mr: 0.5 }}>
                                {profile.name}
                            </Box>
                            <Box component="span" color="text.secondary">
                                invited you to join{' '}
                            </Box>
                            <Link
                                href="/join"
                                underline="hover"
                                color="primary"
                                fontWeight="bold"
                                onClick={(e) => e.stopPropagation()}
                            >
                                PanAm Explorers!
                            </Link>
                        </Typography>
                    </Box>

                    <IconButton 
                        onClick={() => handleToggle()} 
                        size="small" 
                        disableRipple>
            
                        {isExpanded ? <ExpandLessIcon fontSize="small" color="text.secondary"/>
                        : <ExpandMoreIcon fontSize="small" />}
            
                    </IconButton>
                </Box>

                {/* Expandable bio section */}
                <Box
                    sx={{
                        maxHeight: isExpanded ? '90px' : '0px',
                        opacity: isExpanded ? 1 : 0,
                        transition: 'all 0.3s ease-out',
                        overflow: 'hidden',
                        mb: isExpanded ? 1 : 0
                    }}
                >
                    <Box sx={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'space-between',
                        gap: 1}}>

                        {isExpanded && isMobile && (
                            <Avatar 
                                src={profile.profileImageUrl} 
                                alt={profile.name}
                                sx={{ width: 32, height: 32 }}
                            >
                                {profile.name?.charAt(0)}
                            </Avatar>
                        )}

                        <Box sx={{ flex: 1, mt: isMobile ? 0 : 1 }}>
                            <Typography variant="body2" color="text.secondary">
                                {truncatedBio}
                            </Typography>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}